import { formatFileSize } from '../i18n/fileSize';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useRef, useState } from 'react';
import type { Attachment } from '../types';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { isAllowedAttachmentURL } from '../lib/attachment-url';
import { isPreviewableAttachmentContentType } from '../lib/message-attachments';

interface Props {
  attachments: Attachment[];
}

/**
 * Renders message attachments in a Discord-style grid.
 * - 1 image: full width (max 400px)
 * - 2 images: side by side
 * - 3-4 images: 2x2 grid
 * - Non-image files: download cards
 */
export function AttachmentGrid({ attachments }: Props) {
  const { t, i18n } = useLingui();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const closeLightbox = useCallback(() => setLightboxUrl(null), []);
  useDismissibleLayer({
    active: lightboxUrl !== null,
    within: lightboxRef,
    onEscape: closeLightbox,
  });

  const safeAttachments = attachments.filter(a => isAllowedAttachmentURL(a.url));
  const images = safeAttachments.filter(a => isPreviewableAttachmentContentType(a.content_type));
  const files = safeAttachments.filter(a => !isPreviewableAttachmentContentType(a.content_type));

  const gridClass = images.length === 1
    ? 'attachment-grid attachment-grid-1'
    : images.length === 2
      ? 'attachment-grid attachment-grid-2'
      : 'attachment-grid attachment-grid-multi';

  return (
    <>
      {images.length > 0 && (
        <div className={gridClass}>
          {images.map((img) => (
            <button
              key={img.url}
              type="button"
              className="attachment-grid-item"
              aria-label={img.filename ? t`Open image ${img.filename}` : t`Open image attachment`}
              onClick={() => setLightboxUrl(img.url)}
            >
              <img
                src={img.url}
                alt={img.filename || t`Attachment`}
                className="attachment-grid-img"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="attachment-files">
          {files.map((file) => (
            <a
              key={file.url}
              href={file.url}
              download={file.filename}
              className="attachment-file-card"
              aria-label={t`Download ${file.filename}`}
            >
              <span className="attachment-file-icon">📎</span>
              <span className="attachment-file-info">
                <span className="attachment-file-name">{file.filename}</span>
                <span className="attachment-file-size">
                  {file.content_type}
                  {file.size_bytes !== undefined && ` · ${formatFileSize(i18n, file.size_bytes)}`}
                </span>
              </span>
              <span className="attachment-file-download">{t`Download`}</span>
            </a>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div ref={lightboxRef} className="attachment-lightbox" onClick={closeLightbox}>
          <img src={lightboxUrl} alt={t`Full size image`} className="attachment-lightbox-img" />
        </div>
      )}
    </>
  );
}
